import dayjs from "dayjs";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../hooks/redux-hooks";
import { useIsMobileView } from "../../hooks/ui-hook";
import {
  type SchedulePick,
  type ScheduleSlot,
  type TemplateSuggestion,
  useLazyGetTodaySchedulePickQuery,
} from "../../service/schedulePick.service";
import { setCreateModalIntent } from "../../slices/appStateSlice";
import { cacheClients, selectClinicTimezone } from "../../slices/clientSlice";
import { setRecordingDetail } from "../../slices/recordingsSlice";
import type { TClientForm, TRecordingDetailsType } from "../../types/index.type";
import { EventType, trackEvent } from "../../utils/analytics";
import { resolveClinicZone } from "../../utils/clientSchedule";
import { isSuggestionApplied, suggestionOverriddenProps } from "../../utils/sessionTemplate";
import { type EhrSyncPhase, useEhrSync } from "../EhrSyncButton/useEhrSync";
import { type AutopickRecord, type PatientPickSource, findSlot, minutesSince } from "./schedulePick.utils";

export type ChooseMeta = { rank?: number; searchChars?: number };

export type SchedulePickContextValue = {
  /** False only for the default context (no provider above); the drawer always provides one. */
  provided: boolean;
  /** The account can sync a schedule and this drawer is a new document (not edit, not demo). */
  enabled: boolean;
  pick: SchedulePick | null;
  loading: boolean;
  autopick: AutopickRecord | null;
  lastSource: PatientPickSource;
  clinicZone: string;
  slotFor: (clientId: number | null | undefined) => ScheduleSlot | null;
  /** Select a patient (or clear with undefined) and record where the choice came from. */
  choose: (client: TClientForm | undefined, source: PatientPickSource, meta?: ChooseMeta) => void;
  /** "Use schedule pick" from the bar when the sidebar had pre-filled someone else. */
  applySuggestion: () => void;
  /** "Switch" from the bar: the picker opens (dropdown on desktop, sheet on mobile). */
  requestOpenPicker: () => void;
  pickerOpenRequest: number;
  refresh: () => void;
  refreshPhase: EhrSyncPhase;
  refreshCooldownS: number;
  /**
   * Reads the pick for the encounter about to start and returns the call that reports it. Call it
   * while the drawer is still open (starting closes it, and closing resets the pick); report on success.
   */
  prepareStarted: (clientId: number | null | undefined, modality: string, viaNewPatient: boolean) => () => void;
  /** The visit-type template suggestion this drawer put in the template field, while it is there. */
  templateSuggestion: TemplateSuggestion | null;
  /** The clinician picked a template: report a replaced suggestion, and stop suggesting this open. */
  markTemplateTouched: (value: number | string | null | undefined) => void;
  /** ``system_metadata.template_source`` for a note created from the drawer with this template. */
  templateSourceFor: (noteType: number | string | null | undefined) => "suggested" | "drawer";
};

const NOOP_CONTEXT: SchedulePickContextValue = {
  provided: false,
  enabled: false,
  pick: null,
  loading: false,
  autopick: null,
  lastSource: "none",
  clinicZone: "UTC",
  slotFor: () => null,
  choose: () => {},
  applySuggestion: () => {},
  requestOpenPicker: () => {},
  pickerOpenRequest: 0,
  refresh: () => {},
  refreshPhase: "idle",
  refreshCooldownS: 0,
  prepareStarted: () => () => {},
  templateSuggestion: null,
  markTemplateTouched: () => {},
  templateSourceFor: () => "drawer",
};

export const SchedulePickContext = createContext<SchedulePickContextValue>(NOOP_CONTEXT);
export const useSchedulePickContext = () => useContext(SchedulePickContext);

const SETTLED: EhrSyncPhase[] = ["ok", "busy", "refused", "error", "cooldown"];

/**
 * The drawer's schedule pick, one instance per drawer. On every open of a new
 * document it asks the server for today's ranked sheet, pre-selects the suggestion
 * when nothing was pre-filled, and keeps enough per-open state to say afterwards
 * whether the pick held. All events are ids, counts and reasons; no PHI.
 */
export const useSchedulePickController = (): SchedulePickContextValue => {
  const dispatch = useAppDispatch();
  const isMobile = useIsMobileView();
  const userInfo = useAppSelector((state) => state.auth.userInfo);
  const storedTz = useAppSelector(selectClinicTimezone);
  const { isCreateModalVisible, isEditNote, isDemoPath, attachDetailsFor, createModalIntent } = useAppSelector(
    (state) => state.appState,
  );
  const { recordingDetail } = useAppSelector((state) => state.recordings);
  const [trigger] = useLazyGetTodaySchedulePickQuery();
  const sync = useEhrSync();

  const canSync = userInfo?.can_sync_ehr_patients === true;
  const enabled = canSync && !isEditNote && !isDemoPath;

  const [pick, setPick] = useState<SchedulePick | null>(null);
  const [loading, setLoading] = useState(false);
  const [autopick, setAutopick] = useState<AutopickRecord | null>(null);
  const [lastSource, setLastSource] = useState<PatientPickSource>("none");
  const [pickerOpenRequest, setPickerOpenRequest] = useState(0);
  const [templateSuggestion, setTemplateSuggestionState] = useState<TemplateSuggestion | null>(null);

  // Refs mirror state the async paths need without stale closures.
  const openSeqRef = useRef(0);
  const visibleRef = useRef(false);
  const recordingDetailRef = useRef(recordingDetail);
  const pickRef = useRef<SchedulePick | null>(null);
  const autopickRef = useRef<AutopickRecord | null>(null);
  const switchedRef = useRef(false);
  const lastSourceRef = useRef<PatientPickSource>("none");
  const refreshRequestedRef = useRef(false);
  const sizeBeforeRefreshRef = useRef<number | null>(null);
  const isAttachMode = attachDetailsFor !== null;
  // Start Encounter after the clinician turned Quick Start's pick down (×): this open shows
  // the sheet but pre-selects nobody, rather than the patient they just declined.
  const manualRef = useRef(false);
  // The template field, per open: the suggestion in it now, what it held before the suggestion
  // replaced it (put back if the patient changes to one without a suggestion), and whether the
  // clinician has picked a template themselves (after which nothing is suggested).
  const templateSuggestionRef = useRef<TemplateSuggestion | null>(null);
  const beforeSuggestionRef = useRef<Pick<TRecordingDetailsType, "note_type" | "template_id" | "is_template"> | null>(
    null,
  );
  const templateTouchedRef = useRef(false);
  recordingDetailRef.current = recordingDetail;

  const clinicZone = useMemo(() => resolveClinicZone(pick?.clinic_tz ?? storedTz), [pick?.clinic_tz, storedTz]);

  const setTemplateSuggestion = useCallback((suggestion: TemplateSuggestion | null) => {
    templateSuggestionRef.current = suggestion;
    setTemplateSuggestionState(suggestion);
  }, []);

  const setSource = useCallback((source: PatientPickSource) => {
    lastSourceRef.current = source;
    setLastSource(source);
  }, []);

  const applyClient = useCallback(
    (client: TClientForm | undefined) => {
      dispatch(
        setRecordingDetail({
          ...recordingDetailRef.current,
          client_id: client?.id,
          client_uuid: client?.uuid ?? null,
          client_name: client?.name ?? null,
        }),
      );
    },
    [dispatch],
  );

  const openedEvent = useCallback(
    (payload: SchedulePick | null, scheduleState: string, made: AutopickRecord | null, noPickReason: string | null) => {
      trackEvent(EventType.ENCOUNTER_DRAWER_OPENED, {
        source: isAttachMode ? "attach" : manualRef.current ? "manual" : "cta",
        is_mobile: isMobile,
        modality: recordingDetailRef.current?.modality_type ?? null,
        schedule_state: scheduleState,
        schedule_size: payload?.schedule_size ?? 0,
        undocumented_left: payload ? payload.recommended.length + payload.up_next.length : 0,
        candidates_in_window: payload?.candidates_in_window ?? 0,
        autopick: made !== null,
        pick_reason: made?.reason ?? null,
        confidence: made?.confidence ?? null,
        tie_size: made?.tieSize ?? null,
        slot_offset_min: made?.slotOffsetMin ?? null,
        no_pick_reason: noPickReason,
        schedule_age_min: minutesSince(payload?.synced_at),
      });
    },
    [isAttachMode, isMobile],
  );

  /** Pre-select the server's suggestion if the field is still empty. Returns what was made. */
  const maybeAutopick = useCallback(
    (payload: SchedulePick): AutopickRecord | null => {
      const suggestion = payload.suggestion;
      if (!suggestion || recordingDetailRef.current?.client_id != null) return null;
      const slot = findSlot(payload, suggestion.client_id);
      if (!slot) return null;
      applyClient(slot.client);
      const made: AutopickRecord = {
        clientId: suggestion.client_id,
        reason: suggestion.reason,
        confidence: suggestion.confidence,
        tieSize: suggestion.tie_size,
        slotOffsetMin: suggestion.slot_offset_min,
      };
      autopickRef.current = made;
      setAutopick(made);
      setSource("autopick");
      return made;
    },
    [applyClient, setSource],
  );

  const fetchPick = useCallback(
    async (seq: number): Promise<SchedulePick | null> => {
      setLoading(true);
      try {
        const payload = await trigger({ audio_id: attachDetailsFor ?? undefined }, false).unwrap();
        if (seq !== openSeqRef.current || !visibleRef.current) return null;
        pickRef.current = payload;
        setPick(payload);
        const clients = [...payload.recommended, ...payload.up_next, ...payload.documented].map((s) => s.client);
        if (clients.length) dispatch(cacheClients(clients));
        return payload;
      } finally {
        if (seq === openSeqRef.current) setLoading(false);
      }
    },
    [attachDetailsFor, dispatch, trigger],
  );

  // One cycle per drawer open.
  // biome-ignore lint/correctness/useExhaustiveDependencies: runs on open/close only; the rest is read through refs
  useEffect(() => {
    visibleRef.current = isCreateModalVisible;
    if (!isCreateModalVisible) {
      if (createModalIntent !== null) dispatch(setCreateModalIntent(null));
      manualRef.current = false;
      openSeqRef.current += 1;
      pickRef.current = null;
      autopickRef.current = null;
      switchedRef.current = false;
      refreshRequestedRef.current = false;
      setPick(null);
      setAutopick(null);
      setSource("none");
      setLoading(false);
      // A "Switch" request must not outlive the drawer it was made in: the picker mounts fresh
      // on the next open and would otherwise pop its dropdown before the clinician touched it.
      setPickerOpenRequest(0);
      templateTouchedRef.current = false;
      beforeSuggestionRef.current = null;
      setTemplateSuggestion(null);
      return;
    }
    if (isEditNote || isDemoPath) return;
    manualRef.current = createModalIntent === "manual";
    const preset = recordingDetailRef.current?.client_id != null;
    setSource(preset ? "sidebar" : "none");
    if (!canSync) {
      openedEvent(null, "no_sync", null, preset ? "sidebar_selected" : "no_sync");
      return;
    }
    const seq = ++openSeqRef.current;
    fetchPick(seq)
      .then((payload) => {
        if (!payload) return;
        const made = manualRef.current ? null : maybeAutopick(payload);
        const noPick = made
          ? null
          : recordingDetailRef.current?.client_id != null
            ? "sidebar_selected"
            : manualRef.current
              ? "manual"
              : payload.no_pick_reason ?? "unknown";
        openedEvent(payload, payload.state, made, noPick);
      })
      .catch((error) => {
        if (seq !== openSeqRef.current) return;
        console.error("Could not load today's schedule pick:", error);
        openedEvent(null, "error", null, "error");
      });
  }, [isCreateModalVisible]);

  const slotFor = useCallback((clientId: number | null | undefined) => findSlot(pickRef.current, clientId), []);

  // The selected patient's appointment names a template confidently (its visit type): use it, until
  // the clinician picks one. It replaces only what the drawer pre-filled from this device, which is
  // about the previous visit; a hint below the bar is left to the Start card. Runs on the sheet's
  // arrival and on every patient change, autopick or the clinician's.
  const selectedClientId = recordingDetail?.client_id ?? null;
  useEffect(() => {
    if (!enabled || !isCreateModalVisible || !pick || templateTouchedRef.current) return;
    const candidate = findSlot(pick, selectedClientId)?.template_suggestion ?? null;
    const next = candidate && isSuggestionApplied(candidate, pick.template_min_confidence) ? candidate : null;
    const current = templateSuggestionRef.current;
    if (next && current && next.note_type === current.note_type) {
      if (next !== current) setTemplateSuggestion(next);
      return;
    }
    if (next) {
      const detail = recordingDetailRef.current;
      if (!current) {
        beforeSuggestionRef.current = {
          note_type: detail?.note_type,
          template_id: detail?.template_id,
          is_template: detail?.is_template,
        };
      }
      dispatch(
        setRecordingDetail({ ...detail, note_type: next.note_type, template_id: undefined, is_template: false }),
      );
      setTemplateSuggestion(next);
    } else if (current) {
      dispatch(setRecordingDetail({ ...recordingDetailRef.current, ...beforeSuggestionRef.current }));
      beforeSuggestionRef.current = null;
      setTemplateSuggestion(null);
    }
  }, [enabled, isCreateModalVisible, pick, selectedClientId, dispatch, setTemplateSuggestion]);

  const markTemplateTouched = useCallback(
    (value: number | string | null | undefined) => {
      const current = templateSuggestionRef.current;
      templateTouchedRef.current = true;
      if (!current) return;
      if (value !== current.note_type) {
        trackEvent(EventType.TEMPLATE_SUGGESTION_OVERRIDDEN, suggestionOverriddenProps("drawer", current, value, true));
      }
      beforeSuggestionRef.current = null;
      setTemplateSuggestion(null);
    },
    [setTemplateSuggestion],
  );

  const templateSourceFor = useCallback((noteType: number | string | null | undefined): "suggested" | "drawer" => {
    const current = templateSuggestionRef.current;
    return current && noteType === current.note_type ? "suggested" : "drawer";
  }, []);

  const choose = useCallback(
    (client: TClientForm | undefined, source: PatientPickSource, meta: ChooseMeta = {}) => {
      applyClient(client);
      const made = autopickRef.current;
      const resolved: PatientPickSource = !client
        ? "cleared"
        : made && client.id === made.clientId
          ? "autopick"
          : source;
      setSource(resolved);
      if (made && !switchedRef.current && client?.id !== made.clientId) {
        switchedRef.current = true;
        const slot = findSlot(pickRef.current, client?.id);
        trackEvent(EventType.ENCOUNTER_AUTOPICK_SWITCHED, {
          to_source: resolved,
          to_rank: meta.rank ?? null,
          to_slot_offset_min: slot ? minutesSince(slot.scheduled_at) : null,
          to_documented: slot ? slot.documented_at !== null : null,
          tie_size: made.tieSize,
          pick_reason: made.reason,
          confidence: made.confidence,
          search_chars: meta.searchChars ?? 0,
        });
      }
    },
    [applyClient, setSource],
  );

  const applySuggestion = useCallback(() => {
    const slot = findSlot(pickRef.current, pickRef.current?.suggestion?.client_id);
    if (slot) choose(slot.client, "recommended", { rank: 1 });
  }, [choose]);

  const requestOpenPicker = useCallback(() => setPickerOpenRequest((n) => n + 1), []);

  const refresh = useCallback(() => {
    if (!enabled || refreshRequestedRef.current) return;
    refreshRequestedRef.current = true;
    sizeBeforeRefreshRef.current = pickRef.current?.schedule_size ?? null;
    sync.startSync();
  }, [enabled, sync.startSync]);

  // A refresh settles: record it, and on success re-rank (and pre-select if the field is still empty).
  useEffect(() => {
    if (!refreshRequestedRef.current || !SETTLED.includes(sync.phase)) return;
    const phase = sync.phase;
    const before = sizeBeforeRefreshRef.current;
    refreshRequestedRef.current = false;
    if (phase !== "ok") {
      trackEvent(EventType.ENCOUNTER_SCHEDULE_REFRESHED, {
        result: phase,
        schedule_age_min: minutesSince(pickRef.current?.synced_at),
        size_before: before,
        size_after: before,
      });
      return;
    }
    const seq = openSeqRef.current;
    const ageBefore = minutesSince(pickRef.current?.synced_at);
    fetchPick(seq)
      .then((payload) => {
        if (!payload) return;
        if (recordingDetailRef.current?.client_id == null && !manualRef.current) maybeAutopick(payload);
        trackEvent(EventType.ENCOUNTER_SCHEDULE_REFRESHED, {
          result: "ok",
          schedule_age_min: ageBefore,
          size_before: before,
          size_after: payload.schedule_size,
        });
      })
      .catch((error) => console.error("Could not reload today's schedule pick:", error));
  }, [sync.phase, fetchPick, maybeAutopick]);

  // The properties are read now, not when the event is sent: the record and dictate paths close
  // the drawer before they finish, and the close resets every ref below.
  const prepareStarted = useCallback(
    (clientId: number | null | undefined, modality: string, viaNewPatient: boolean) => {
      if (isEditNote || isDemoPath) return () => {};
      const made = autopickRef.current;
      const payload = pickRef.current;
      const source: PatientPickSource = viaNewPatient
        ? "new_patient"
        : clientId == null
          ? "none"
          : lastSourceRef.current;
      const slot = findSlot(payload, clientId);
      const properties = {
        patient_source: source,
        autopick_shown: made !== null,
        autopick_kept: made !== null && clientId === made.clientId,
        pick_reason: made?.reason ?? null,
        confidence: made?.confidence ?? null,
        tie_size: made?.tieSize ?? null,
        was_on_schedule: slot !== null,
        slot_offset_min: slot ? minutesSince(slot.scheduled_at, dayjs()) : null,
        schedule_state: canSync ? payload?.state ?? "unknown" : "no_sync",
        modality,
        source: isAttachMode ? "attach" : manualRef.current ? "manual" : "cta",
      };
      return () => trackEvent(EventType.ENCOUNTER_STARTED, properties);
    },
    [canSync, isAttachMode, isDemoPath, isEditNote],
  );

  return useMemo(
    () => ({
      provided: true,
      enabled,
      pick,
      loading,
      autopick,
      lastSource,
      clinicZone,
      slotFor,
      choose,
      applySuggestion,
      requestOpenPicker,
      pickerOpenRequest,
      refresh,
      refreshPhase: sync.phase,
      refreshCooldownS: sync.cooldownRemainingS,
      prepareStarted,
      templateSuggestion,
      markTemplateTouched,
      templateSourceFor,
    }),
    [
      enabled,
      pick,
      loading,
      autopick,
      lastSource,
      clinicZone,
      slotFor,
      choose,
      applySuggestion,
      requestOpenPicker,
      pickerOpenRequest,
      refresh,
      sync.phase,
      sync.cooldownRemainingS,
      prepareStarted,
      templateSuggestion,
      markTemplateTouched,
      templateSourceFor,
    ],
  );
};
